import React from 'react';

import { Box, Chip, Stack, Typography } from '@mui/material';

interface Props {
  category: string;
  status: boolean | null;
  activeLabel: string;
  clearLabel: string;
  selected: boolean;
  onClick: () => void;
}

export default function StatusColumn({ category, status, activeLabel, clearLabel, selected, onClick }: Props) {
  const isClickable = status !== false;

  const chipProps =
    status === null
      ? { label: 'Unknown', color: 'default' as const, variant: 'outlined' as const }
      : status
      ? { label: activeLabel, color: 'error' as const, variant: 'filled' as const }
      : { label: clearLabel, color: 'success' as const, variant: 'outlined' as const };

  return (
    <Box
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        flex: 1,
        minWidth: 0,
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        cursor: isClickable ? 'pointer' : 'default',
        textAlign: 'center',
        bgcolor: selected ? 'action.selected' : 'transparent',
        '&:hover': isClickable ? { bgcolor: selected ? 'action.selected' : 'action.hover' } : {},
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      <Stack alignItems="center" spacing={1}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={500}
          textTransform="uppercase"
          letterSpacing={0.5}
        >
          {category}
        </Typography>
        <Chip {...chipProps} size="small" sx={{ fontWeight: 500, height: 24 }} />
      </Stack>
    </Box>
  );
}
